// Presentation helpers for the billing subscription summary returned by
// GET /billing/me/subscription-summary. All billing decisions (which
// subscription is current, account status, remaining days) are made on the
// server; this file only picks labels for what the server returned.

export const SUBSCRIPTION_SUMMARY_BATCH_SIZE = 200;

// state: "loading" | "error" | "ready"; summary is null when the server
// returned nothing for this restaurant (outside the caller's billing scope).
export const getStoreSubscription = (subscriptionState, storeId) => {
  const status = subscriptionState?.status || "loading";
  if (!storeId || status === "idle" || status === "loading")
    return { state: "loading", summary: null };
  if (status === "error") return { state: "error", summary: null };
  const summary = subscriptionState.byStoreId?.[storeId] || null;
  return { state: summary ? "ready" : "unavailable", summary };
};

export const describeSubscription = (subscription, text) => {
  const t = text.subscription;
  const { state, summary } = subscription || {};
  if (state !== "ready" || !summary) {
    return { ready: false, message: t[state] || t.loading };
  }
  const subscriptionKey =
    summary.isCurrent && summary.periodEnded
      ? "periodEnded"
      : summary.subscriptionStatus;
  return {
    ready: true,
    accountStatus:
      t.accountStatuses[summary.accountStatus] || t.accountStatuses.inactive,
    subscriptionStatus: t.statuses[subscriptionKey] || t.statuses.none,
    planCode: summary.plan?.code || "",
    startDate: summary.startDate || "",
    endDate: summary.endDate || "",
    remainingDays:
      typeof summary.remainingDays === "number" ? summary.remainingDays : null,
    cancellationEffectiveAt: summary.cancellationEffectiveAt || "",
    // branch coverage computed by the server from the actual subscription and branch ids (null when unavailable)
    coverage: summary.branchCoverage || null,
    storeId: summary.storeId || "",
  };
};

// Sums dashboard-stats responses from several store-id batches. Every figure
// is an additive count/amount computed by the server, so summing batches
// gives the same result as one request.
export const mergeDashboardBillingStats = (parts = []) => {
  const addMap = (target, source = {}) =>
    Object.entries(source).forEach(([key, value]) => {
      target[key] = (target[key] || 0) + (Number(value) || 0);
    });
  return parts.reduce(
    (total, part) => {
      if (!part) return total;
      total.restaurantCount += part.restaurantCount || 0;
      total.invoices.count += part.invoices?.count || 0;
      total.invoices.totalMinor += part.invoices?.totalMinor || 0;
      total.invoices.paidMinor += part.invoices?.paidMinor || 0;
      total.invoices.unpaidMinor += part.invoices?.unpaidMinor || 0;
      addMap(total.invoices.byStatus, part.invoices?.byStatus);
      total.subscriptions.count += part.subscriptions?.count || 0;
      total.subscriptions.withoutSubscription +=
        part.subscriptions?.withoutSubscription || 0;
      total.subscriptions.expiringSoon += part.subscriptions?.expiringSoon || 0;
      addMap(total.subscriptions.byStatus, part.subscriptions?.byStatus);
      return total;
    },
    {
      restaurantCount: 0,
      invoices: {
        count: 0,
        totalMinor: 0,
        paidMinor: 0,
        unpaidMinor: 0,
        byStatus: {},
      },
      subscriptions: {
        count: 0,
        withoutSubscription: 0,
        expiringSoon: 0,
        byStatus: {},
      },
    },
  );
};

// Amounts arrive in minor units (halalas); shown in SAR with Latin digits
// like the rest of the dashboard.
export const formatSarFromMinor = (minor) =>
  (Number(minor || 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// Non-zero groups only, labelled from the dashboard translation table.
export const billingStatusGroups = (byStatus = {}, labels = {}, extra = {}) =>
  Object.entries({ ...byStatus, ...extra })
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({ key, label: labels[key] || key, value }));

// Same behaviour as the billing screens' formatDate (UTC, since billing
// period dates are UTC days).
export const formatDate = (value, lang = "ar") => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "ar-SA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

// One cashier card (platform or integration) from the shared per-restaurant
// subscription state. kind: "platform" | "integration".
export const describeSubscriptionCard = (subscription, kind, text, lang) => {
  const t = text.subscription;
  const { state, summary } = subscription || {};
  if (state !== "ready" || !summary) {
    return {
      days: state === "error" ? "—" : state === "loading" ? "…" : "0",
      caption: t[state] || t.loading,
      note: "",
      warning: true,
    };
  }
  const entry = kind === "integration" ? summary.integration : summary;
  if (!entry || !entry.hasSubscription) {
    return { days: "0", caption: t.statuses.none, note: "", warning: true };
  }
  const days =
    typeof entry.remainingDays === "number" ? entry.remainingDays : 0;
  const healthy = entry.subscriptionStatus === "active" && !entry.periodEnded;
  const statusKey =
    entry.isCurrent && entry.periodEnded
      ? "periodEnded"
      : entry.subscriptionStatus;
  return {
    days: String(days),
    caption: t.daysRemaining.replace("{days}", days),
    note: entry.endDate
      ? `${t.endsOn}: ${formatDate(entry.endDate, lang)}`
      : "",
    status: healthy ? "" : t.statuses[statusKey] || "",
    warning: !healthy,
  };
};
